import React, { useState, useEffect } from 'react';
import { FaPhone, FaEnvelope, FaWhatsapp, FaHistory, FaLock, FaCrown } from 'react-icons/fa';
import api from '../../services/api';

const ContactReveals = () => {
    const [stats, setStats] = useState({ contactsRevealed: 0, remaining: 0, total: 0 });
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);

    // SAME HELPER USED IN ALL OTHER PAGES (Matches, Home, Shortlisted, ViewedProfiles)
    const getProfileImage = (imagePath, gender) => {
        if (!imagePath) {
            return gender === 'Female'
                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                : 'https://randomuser.me/api/portraits/men/85.jpg';
        }

        if (imagePath.startsWith('http') || imagePath.startsWith('data:') || imagePath.startsWith('blob:')) {
            return imagePath;
        }

        const BASE_URL = api.defaults.baseURL;
        const baseUrl = BASE_URL.replace("/api", "");
        const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
        return `${baseUrl}${cleanPath}`;
    };

    useEffect(() => {
        fetchRevealedContacts();
        window.scrollTo(0, 0);
    }, []);

    const fetchRevealedContacts = async () => {
        try {
            const response = await api.get('/revealed-contacts');
            setContacts(response.data.contacts || []);
            setStats(response.data.stats || { contactsRevealed: 0, remaining: 0, total: 0 });
        } catch (error) {
            console.error('Error fetching revealed contacts:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const revealedDate = new Date(date);
        const diffMs = now - revealedDate;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
        if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
                    <p className="mt-4 text-text-secondary">Loading revealed contacts...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-[rgba(255,255,255,0.03)] overflow-hidden">
            {/* Header Section */}
            <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass shadow-sm border-b border-glass-border p-6 z-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 w-full mx-auto w-full">
                    <div>
                        <h2 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                            <FaPhone className="text-primary-pink text-3xl" /> Contact Reveals
                        </h2>
                        <p className="text-text-muted text-sm mt-1">Manage and view the contact details you've unlocked.</p>
                    </div>

                    {/* Stats Cards */}
                    <div className="flex gap-4">
                        <div className="bg-[rgba(255,46,136,0.1)] px-4 py-2 rounded-xl border border-glass-border flex items-center gap-3">
                            <div className="bg-[rgba(255,46,136,0.15)] p-2 rounded-lg text-primary-pink">
                                <FaHistory />
                            </div>
                            <div>
                                <p className="text-xs text-text-muted font-bold uppercase">Revealed</p>
                                <p className="text-lg font-black text-text-primary">{stats.contactsRevealed}</p>
                            </div>
                        </div>
                        <div className="bg-[rgba(34,197,94,0.1)] px-4 py-2 rounded-xl border border-glass-border flex items-center gap-3">
                            <div className="bg-[rgba(34,197,94,0.2)] p-2 rounded-lg text-success-green">
                                <FaLock />
                            </div>
                            <div>
                                <p className="text-xs text-text-muted font-bold uppercase">Remaining</p>
                                <p className="text-lg font-black text-text-primary">{stats.remaining}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 lg:p-8">
                <div className="w-full mx-auto w-full">
                    <h3 className="text-lg font-bold text-text-primary mb-6 flex items-center gap-2">
                        <FaHistory className="text-text-muted" /> Recent Unlocks
                    </h3>

                    {contacts.length === 0 ? (
                        <div className="text-center py-12 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-2xl">
                            <div className="w-16 h-16 bg-[rgba(255,255,255,0.05)] rounded-full flex items-center justify-center mx-auto mb-4 text-text-muted text-2xl">
                                <FaPhone />
                            </div>
                            <h3 className="text-lg font-bold text-text-primary">No contacts revealed yet</h3>
                            <p className="text-text-muted mt-2">Start connecting with matches to see their contact info here.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {contacts.map(contact => {
                                // Assuming gender might be available, fallback to Male if not
                                const gender = contact.gender || 'Male';

                                return (
                                    <div key={contact.id || contact._id} className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-2xl overflow-hidden hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all group">
                                        <div className="p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={getProfileImage(contact.image, gender)}
                                                        alt={contact.name}
                                                        onError={(e) => {
                                                            e.currentTarget.onerror = null;
                                                            e.currentTarget.src = gender === 'Female'
                                                                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                                                                : 'https://randomuser.me/api/portraits/men/85.jpg';
                                                        }}
                                                        className="w-12 h-12 rounded-full object-cover border-2 border-dark-navy shadow-[0_0_10px_rgba(255,255,255,0.1)]"
                                                    />
                                                    <div>
                                                        <h4 className="font-bold text-text-primary group-hover:text-primary-pink transition-colors">
                                                            {contact.name}
                                                        </h4>
                                                        <p className="text-xs text-text-muted">
                                                            {contact.age ? `${contact.age} yrs, ` : ''}{contact.location || 'Location not set'}
                                                        </p>
                                                    </div>
                                                </div>
                                                {contact.isPremium && (
                                                    <span className="bg-gradient-to-r from-amber-400 to-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
                                                        <FaCrown size={10} /> ELITE
                                                    </span>
                                                )}
                                            </div>

                                            <div className="space-y-3 bg-[rgba(255,255,255,0.03)] rounded-xl p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3 text-text-secondary">
                                                        <div className="w-8 h-8 rounded-full bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass flex items-center justify-center text-green-500 shadow-sm">
                                                            <FaPhone className="text-sm" />
                                                        </div>
                                                        <span className="font-mono font-medium text-sm">{contact.mobile || 'N/A'}</span>
                                                    </div>
                                                    <a
                                                        href={`tel:${contact.mobile}`}
                                                        className="text-xs font-bold text-green-600 hover:underline"
                                                    >
                                                        CALL
                                                    </a>
                                                </div>

                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3 text-text-secondary">
                                                        <div className="w-8 h-8 rounded-full bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass flex items-center justify-center text-green-500 shadow-sm">
                                                            <FaWhatsapp className="text-sm" />
                                                        </div>
                                                        <span className="font-mono font-medium text-sm">{contact.mobile || 'N/A'}</span>
                                                    </div>
                                                    <a
                                                        href={`https://wa.me/${(contact.mobile || '').replace(/[^0-9]/g, '')}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs font-bold text-green-600 hover:underline"
                                                    >
                                                        CHAT
                                                    </a>
                                                </div>

                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3 text-text-secondary">
                                                        <div className="w-8 h-8 rounded-full bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass flex items-center justify-center text-blue-500 shadow-sm">
                                                            <FaEnvelope className="text-sm" />
                                                        </div>
                                                        <span className="font-medium text-sm truncate max-w-[180px]">{contact.email || 'N/A'}</span>
                                                    </div>
                                                    <a
                                                        href={`mailto:${contact.email}`}
                                                        className="text-xs font-bold text-blue-400 hover:underline"
                                                    >
                                                        EMAIL
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-[rgba(255,255,255,0.03)] px-6 py-3 border-t border-glass-border flex justify-between items-center">
                                            <span className="text-xs text-text-muted">{contact.occupation || 'Occupation not set'}</span>
                                            {/* <button className="text-sm font-bold text-primary-pink hover:text-primary-pink transition-colors">
                                                View Profile
                                            </button> */}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactReveals;